import { useState } from 'react';
import { API_URL } from '../config';
import { removeFreeGift } from '../api/freeGift.api';
import { toast } from './Toast';

const formatKES = (n) =>
    'KES ' + Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 });

function resolveImage(path) {
    if (!path) return null;
    return path.startsWith('http') ? path : `${API_URL}/${path}`;
}

/**
 * Shows the free-gift progress bar + selected gift badges.
 * Props:
 *   giftEvaluation – object from cart.giftEvaluation
 *   onOpenGiftModal – callback to open GiftSelectionModal
 *   onGiftRemoved   – callback after a gift is removed (triggers cart refresh)
 */
export default function CartGiftProgress({ giftEvaluation, onOpenGiftModal, onGiftRemoved }) {
    const [removing, setRemoving] = useState({});

    if (!giftEvaluation) return null;

    const { currentSlab, nextSlab, progressPercent, selectedGifts } = giftEvaluation;

    const handleRemove = async (variantId) => {
        setRemoving((prev) => ({ ...prev, [variantId]: true }));
        try {
            await removeFreeGift(variantId);
            toast.success('Free gift removed');
            onGiftRemoved?.();
        } catch {
            toast.error('Failed to remove gift');
        } finally {
            setRemoving((prev) => ({ ...prev, [variantId]: false }));
        }
    };

    return (
        <div className="bg-card border border-border rounded-xl p-3 my-3">
            {/* Header */}
            <div className="flex items-center gap-2 mb-2.5">
                <i className="ri-gift-line text-primary text-lg"></i>
                <h3 className="text-sm font-semibold text-foreground">Free Gift with Purchase</h3>
            </div>

            {/* Progress bar toward next slab */}
            {nextSlab && (
                <div className="mb-3">
                    <div className="flex justify-between text-xs mb-1">
                        <span className="text-muted-foreground">
                            {currentSlab
                                ? `Spend ${formatKES(nextSlab.amountNeeded)} more to upgrade your gift`
                                : `Spend ${formatKES(nextSlab.amountNeeded)} more to unlock a free gift worth up to ${formatKES(nextSlab.maxFreeValue)}!`}
                        </span>
                        <span className="text-primary font-semibold">{progressPercent}%</span>
                    </div>
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                        <div
                            className="h-full bg-primary rounded-full transition-all duration-500"
                            style={{ width: `${progressPercent}%` }}
                        />
                    </div>
                    {!currentSlab && (
                        <p className="text-xs text-muted-foreground mt-1.5">
                            Unlock a <strong className="text-primary">FREE accessory worth up to {formatKES(nextSlab.maxFreeValue)}</strong> when you spend {formatKES(nextSlab.minCartValue)}!
                        </p>
                    )}
                </div>
            )}

            {/* Unlocked — show CTA or selected gifts */}
            {currentSlab && (
                <div>
                    {selectedGifts?.length > 0 ? (
                        <div>
                            <p className="text-xs text-success font-semibold mb-2 flex items-center gap-1">
                                <i className="ri-checkbox-circle-fill"></i>
                                Your free gift{selectedGifts.length > 1 ? 's are' : ' is'} added!
                            </p>
                            <div className="space-y-2">
                                {selectedGifts.map((gift) => {
                                    const imgSrc = resolveImage(gift.image);
                                    return (
                                        <div
                                            key={gift.variantId}
                                            className="flex items-center gap-3 bg-success/10 border border-success/30 rounded-lg p-2"
                                        >
                                            <div className="w-10 h-10 bg-card rounded overflow-hidden flex-shrink-0 border border-border">
                                                {imgSrc ? (
                                                    <img src={imgSrc} alt={gift.name} className="w-full h-full object-contain p-0.5" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <i className="ri-image-line text-muted-foreground/40 text-sm"></i>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-semibold text-foreground truncate">{gift.name}</p>
                                                <div className="flex items-center gap-1 mt-0.5">
                                                    <span className="text-[10px] bg-success/15 text-success font-bold px-1.5 py-0.5 rounded">FREE GIFT</span>
                                                    <span className="text-[10px] text-muted-foreground line-through">{formatKES(gift.price)}</span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleRemove(gift.variantId)}
                                                disabled={removing[gift.variantId]}
                                                className="w-6 h-6 flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                                                title="Remove gift"
                                            >
                                                {removing[gift.variantId]
                                                    ? <span className="w-3 h-3 border-2 border-border border-t-primary rounded-full animate-spin inline-block"></span>
                                                    : <i className="ri-close-line text-sm"></i>
                                                }
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Allow changing if quota not met */}
                            {selectedGifts.length < (currentSlab.maxQuantity || 1) && (
                                <button
                                    onClick={() => onOpenGiftModal?.(currentSlab)}
                                    className="mt-2 text-xs text-primary underline hover:no-underline"
                                >
                                    Choose another gift
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 text-center">
                            <i className="ri-gift-fill text-primary text-2xl mb-1 block"></i>
                            <p className="text-sm font-semibold text-foreground mb-0.5">
                                You've unlocked a FREE accessory!
                            </p>
                            <p className="text-xs text-muted-foreground mb-3">
                                Choose any accessory worth up to <strong className="text-primary">{formatKES(currentSlab.maxFreeValue)}</strong>
                            </p>
                            <button
                                onClick={() => onOpenGiftModal?.(currentSlab)}
                                className="px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:brightness-110 transition-all"
                            >
                                <i className="ri-gift-line mr-1"></i>
                                Choose Your Free Gift
                            </button>
                        </div>
                    )}

                    {/* Progress toward next tier */}
                    {nextSlab && (
                        <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
                            Spend <strong className="text-primary">{formatKES(nextSlab.amountNeeded)}</strong> more to upgrade to a gift worth up to <strong className="text-foreground">{formatKES(nextSlab.maxFreeValue)}</strong>
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
